import moment from "moment";
import type {
  CalendarEventType,
  TimeBlockEvent,
} from "../../../../types/calendar";
import type { CalendarEvent } from "../../../../types/transactions";
import { salonWallClockToISO } from "../../../../utils/timeUtils";
import {
  NO_REPEAT,
  RepeatRule,
  occurrenceDays,
  repeatEndError,
  rulePayload,
} from "./repeatRule";

/** The repo's time block types (not renamed; decided 2026-10-08). */
export const TIME_BLOCK_TYPES = [
  "Break",
  "Vacation",
  "Sick",
  "Personal",
  "Holiday",
  "Other",
] as const;

/** Series scopes (Google Calendar pattern) → the API's `applies_to`. */
export const SERIES_SCOPES: {
  value: "single" | "hereafter" | "all";
  label: string;
}[] = [
  { value: "single", label: "This event" },
  { value: "hereafter", label: "This and following events" },
  { value: "all", label: "All events" },
];

export interface TimeBlockFormState {
  staffId: string;
  type: string;
  description: string;
  startDay: string;
  startTime: string;
  endDay: string;
  endTime: string;
  rule: RepeatRule;
}

const DAY = "YYYY-MM-DD";
const pad = (n: number) => String(n).padStart(2, "0");
/** Fake-local grid Date → "YYYY-MM-DD" / "HH:mm" with native getters. */
const dayOf = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const timeOf = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

export const addMinutesToTime = (hhmm: string, mins: number): string => {
  const [h, m] = hhmm.split(":").map(Number);
  const t = Math.min(23 * 60 + 45, (h || 0) * 60 + (m || 0) + mins);
  return `${pad(Math.floor(t / 60))}:${pad(t % 60)}`;
};

/** Initial form: an existing block (edit), a slot / seed, or the viewed day. */
export function initialTimeBlockForm(
  data:
    | TimeBlockEvent
    | { startTime?: string; resourceId?: string; date?: Date }
    | null
    | undefined,
  viewedDay: Date,
): TimeBlockFormState {
  if (data && "isTimeBlock" in data && data.isTimeBlock) {
    return {
      staffId: data.resourceId || "",
      type: data.type || "",
      description: data.description || "",
      startDay: dayOf(data.start),
      startTime: timeOf(data.start),
      endDay: dayOf(data.end),
      endTime: timeOf(data.end),
      rule: NO_REPEAT(dayOf(data.start)),
    };
  }
  const seed = data as { startTime?: string; resourceId?: string; date?: Date };
  const day = dayOf(seed?.date ?? viewedDay);
  const start = seed?.startTime || "12:00";
  return {
    staffId: seed?.resourceId || "",
    type: "",
    description: "",
    startDay: day,
    startTime: start,
    endDay: day,
    endTime: addMinutesToTime(start, 60),
    rule: NO_REPEAT(day),
  };
}

export type TimeBlockErrors = Partial<Record<keyof TimeBlockFormState, string>>;

export function validateTimeBlock(
  f: TimeBlockFormState,
  isEdit: boolean,
): TimeBlockErrors {
  const e: TimeBlockErrors = {};
  if (!isEdit && !f.staffId) e.staffId = "Please select a staff member";
  if (!f.type) e.type = "Pick a type";
  if (!f.startDay || !f.startTime) e.startTime = "Start is required";
  if (!f.endDay || !f.endTime) e.endTime = "End is required";
  if (
    f.startDay &&
    f.endDay &&
    `${f.endDay} ${f.endTime}` <= `${f.startDay} ${f.startTime}`
  ) {
    e.endTime = "End must be after the start";
  }
  // A Repeat end left stale when the start / end dates moved. Edits don't
  // send the rule, so they skip it.
  const repeatError = isEdit
    ? undefined
    : repeatEndError(f.rule, f.startDay, f.endDay);
  if (repeatError) e.rule = repeatError;
  return e;
}

/**
 * The existing time-off payload: create sends staff + the Repeat rule's
 * interval / unit / end date; edit sends the block id + `applies_to` (the
 * backend can't change a rule or the staff member on edit).
 */
export function buildTimeBlockPayload(
  f: TimeBlockFormState,
  opts: {
    timezone: string | undefined;
    isEdit: boolean;
    id?: string;
    appliesTo?: "single" | "hereafter" | "all";
  },
): Record<string, unknown> {
  const base = {
    type: f.type,
    description: f.description,
    start_dt: salonWallClockToISO(f.startDay, f.startTime, opts.timezone ?? ""),
    end_dt: salonWallClockToISO(f.endDay, f.endTime, opts.timezone ?? ""),
  };
  if (opts.isEdit) {
    return { ...base, id: opts.id, applies_to: opts.appliesTo ?? "single" };
  }
  return { ...base, staff_id: f.staffId, ...rulePayload(f.rule, f.startDay) };
}

export interface TimeBlockConflict {
  id: string;
  label: string;
}

const minutesOfDay = (d: Date) => d.getHours() * 60 + d.getMinutes();
const toMin = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

/**
 * Appointments (loaded calendar events) the block would sit on, for every
 * occurrence of its rule (client-side; only what the calendar has loaded).
 * Single-day blocks only compare times; multi-day blocks compare the span.
 */
export function timeBlockConflicts(
  f: TimeBlockFormState,
  events: CalendarEventType[],
  excludeId?: string,
): TimeBlockConflict[] {
  if (!f.staffId || !f.startTime || !f.endTime) return [];
  const spanDays = moment(f.endDay, DAY).diff(moment(f.startDay, DAY), "days");
  const days = occurrenceDays(f.rule, f.startDay);
  const out: TimeBlockConflict[] = [];
  for (const e of events) {
    if (e.isTimeBlock || e.id === excludeId || e.resourceId !== f.staffId) {
      continue;
    }
    const a = e as CalendarEvent;
    if (a.status === "Cancelled" || a.status === "Declined") continue;
    const eDay = dayOf(new Date(e.start));
    const eStart = minutesOfDay(new Date(e.start));
    const eEnd =
      eStart +
      Math.round(
        (new Date(e.end).getTime() - new Date(e.start).getTime()) / 60000,
      );
    const hit = days.some((d) => {
      const last = moment(d, DAY).add(spanDays, "days").format(DAY);
      if (eDay < d || eDay > last) return false;
      const from = eDay === d ? toMin(f.startTime) : 0;
      const to = eDay === last ? toMin(f.endTime) : 24 * 60;
      return eStart < to && from < eEnd;
    });
    if (hit) {
      const who =
        a.firstName || a.lastName
          ? `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim()
          : e.title;
      out.push({
        id: e.id,
        label: `${who} · ${moment(eDay, DAY).format("ddd, MMM D")} · ${moment(
          `${eDay} ${pad(Math.floor(eStart / 60))}:${pad(eStart % 60)}`,
          "YYYY-MM-DD HH:mm",
        ).format("h:mm A")}`,
      });
    }
  }
  return out;
}

/** Part of a recurring series (rule is read-only, edits / deletes ask scope). */
export const isSeries = (e: TimeBlockEvent | null | undefined): boolean =>
  !!(e?.recurrenceParentId || e?.recurrenceInterval);

/** "YYYY-MM-DD" + "HH:mm" → a fake-local grid Date (native, not moment's
 * default zone). */
const wallClockDate = (day: string, time: string): Date | null => {
  const [y, mo, d] = day.split("-").map(Number);
  const [h, m] = time.split(":").map(Number);
  if (!y || !mo || !d || Number.isNaN(h) || Number.isNaN(m)) return null;
  return new Date(y, mo - 1, d, h, m);
};

/** Id of the new block's grid preview (`draftTimeBlockEvent`). */
export const TIME_BLOCK_DRAFT_ID = "time-block-draft";

/**
 * The new block's grid preview while the drawer is open: its staff column
 * and first occurrence, drawn as a dashed outline. Null until staff and a
 * valid start / end are set.
 */
export function draftTimeBlockEvent(
  form: TimeBlockFormState,
): TimeBlockEvent | null {
  if (!form.staffId) return null;
  const start = wallClockDate(form.startDay, form.startTime);
  const end = wallClockDate(form.endDay, form.endTime);
  if (!start || !end || end <= start) return null;
  return {
    id: TIME_BLOCK_DRAFT_ID,
    title: form.type || "Time block",
    start,
    end,
    resourceId: form.staffId,
    isTimeBlock: true,
    isTransient: true,
    type: form.type,
    description: form.description,
  };
}
